// src/pages/OnboardingNew.js
import React, { useState } from "react";
import TextInput from "../components/TextInput";
import FileUploader from "../components/FileUploader";
import SignaturePad from "../components/SignaturePad";
import TermsModal from "../components/TermsModal";
// 1) Import the API function
import { submitOnboardingNew } from "../services/api";

function OnboardingNew() {
  const [formData, setFormData] = useState({
    // Core required fields
    first_name: "",
    last_name: "",
    email1: "",
    email2: "",
    phone_number: "",

    // Additional fields (all considered required)
    date_of_birth: "",
    address_line_1: "",
    address_line_2: "",
    address_city: "",
    postcode: "",
    county: "",
    occupation: "",
    working_hours: "",
    payment_full_name: "",
    payment_sort_code: "",
    payment_account_number: "",
    referral1: "",
    referral2: "",

    // Booleans
    high_demand_events: false,
    buying_more_tickets: false,
    transfer_service: false,
    allow_tt_signups: false,
    all_tt_manage_passwords: false,
    allow_tt_create_emails: false,
    live_rebate_updates: false,

    // Required T&C acceptance
    terms_accepted: false,

    // File & Signature
    file: null,
    signature_base64: null,
  });

  // Control the T&C modal visibility
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Handle text or checkbox changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Handle file selection
  const handleFileSelected = (file) => {
    setFormData((prev) => ({ ...prev, file }));
  };

  // Handle signature changes (base64 string from SignaturePad)
  const handleSignatureChange = (signatureData) => {
    setFormData((prev) => ({ ...prev, signature_base64: signatureData }));
  };

  // Open/close the T&C modal
  const openModal = (e) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Called by the modal when user accepts T&C
  const acceptTerms = () => {
    setFormData((prev) => ({ ...prev, terms_accepted: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 1. Check all required text fields
    const requiredTextFields = [
      "first_name",
      "last_name",
      "email1",
      "phone_number",
      "date_of_birth",
      "address_line_1",
      "address_line_2",
      "address_city",
      "postcode",
      "county",
      "occupation",
      "working_hours",
      "payment_full_name",
      "payment_sort_code",
      "payment_account_number",
      // "referral1",
    ];

    for (const field of requiredTextFields) {
      if (!formData[field].trim()) {
        alert(`Please fill out the "${field}" field.`);
        return;
      }
    }

    // 2. Check file and signature
    if (!formData.file) {
      alert("Please upload the required ID document.");
      return;
    }
    if (!formData.signature_base64) {
      alert("Please provide your signature.");
      return;
    }

    // 3. Terms & Conditions
    if (!formData.terms_accepted) {
      alert("You must accept the Terms & Conditions!");
      return;
    }

    // If all checks pass, proceed
    console.log("Submitting Onboarding (New) form:", formData);

    // 4. Call the API
    try {
      const result = await submitOnboardingNew(formData);
      alert(`Onboarding (New) submitted successfully! ID: ${result.id}`);
      // Optionally reset the form or redirect to a success page here
    } catch (error) {
      console.error("Submission error:", error);
      alert("Submission failed. Please try again or contact support.");
    }
  };

  return (
    <div style={{ maxWidth: "700px", margin: "0 auto" }}>
      <h1>Onboarding - New Customer</h1>
      <form onSubmit={handleSubmit}>
        {/* ---- Basic Information ---- */}
        <div style={{ marginBottom: "1rem" }}>
          <strong>Basic Information</strong>
        </div>
        <TextInput
          label="First Name (required)"
          name="first_name"
          value={formData.first_name}
          onChange={handleChange}
        />
        <TextInput
          label="Last Name (required)"
          name="last_name"
          value={formData.last_name}
          onChange={handleChange}
        />
        <TextInput
          label="Primary Email (required)"
          name="email1"
          value={formData.email1}
          onChange={handleChange}
          type="email"
        />
        <TextInput
          label="Phone Number (required)"
          name="phone_number"
          value={formData.phone_number}
          onChange={handleChange}
          type="tel"
        />

        <TextInput
          label="Date of Birth (required)"
          name="date_of_birth"
          value={formData.date_of_birth}
          onChange={handleChange}
          type="date"
        />
        <TextInput
          label="Address Line 1 (required)"
          name="address_line_1"
          value={formData.address_line_1}
          onChange={handleChange}
        />
        <TextInput
          label="Address Line 2 (required)"
          name="address_line_2"
          value={formData.address_line_2}
          onChange={handleChange}
        />
        <TextInput
          label="City (required)"
          name="address_city"
          value={formData.address_city}
          onChange={handleChange}
        />
        <TextInput
          label="Postcode (required)"
          name="postcode"
          value={formData.postcode}
          onChange={handleChange}
        />
        <TextInput
          label="County (required)"
          name="county"
          value={formData.county}
          onChange={handleChange}
        />
        <TextInput
          label="Current Occupation/Job (required)"
          name="occupation"
          value={formData.occupation}
          onChange={handleChange}
        />
        <TextInput
          label="Working Hours (required)"
          name="working_hours"
          value={formData.working_hours}
          onChange={handleChange}
        />

        {/* ---- User Permissions and Notifications ---- */}
        <div style={{ margin: "1rem 0 0.5rem" }}>
          <strong>User Permissions and Notifications</strong>
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <label>
            <input
              type="checkbox"
              name="high_demand_events"
              checked={formData.high_demand_events}
              onChange={handleChange}
            />
            &nbsp;<strong>Receive Advice on High-Demand Events</strong>
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;I agree to receive advice and notifications from TT regarding high-demand events
          </label>
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <label>
            <input
              type="checkbox"
              name="buying_more_tickets"
              checked={formData.buying_more_tickets}
              onChange={handleChange}
            />
            &nbsp;<strong>Receive Advice on Buying More Tickets Within Promoter Limits</strong>
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;I agree to receive guidance from TT on how
            to purchase additional tickets while complying with promoter limits.
          </label>
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <label>
            <input
              type="checkbox"
              name="transfer_service"
              checked={formData.transfer_service}
              onChange={handleChange}
            />
            &nbsp;<strong>Transfer Service: Allow TT to Deliver Tickets to Customers</strong>
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;I consent to TT International handling the
            delivery of tickets to customers on my behalf.
          </label>
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <label>
            <input
              type="checkbox"
              name="allow_tt_signups"
              checked={formData.allow_tt_signups}
              onChange={handleChange}
            />
            &nbsp;<strong>Allow TT to Complete Sign-Ups on My Behalf</strong>
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;I authorize TT International to complete
            ticketing sign-ups on my behalf for ticket purchases and transfers.
          </label>
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <label>
            <input
              type="checkbox"
              name="all_tt_manage_passwords"
              checked={formData.all_tt_manage_passwords}
              onChange={handleChange}
            />
            &nbsp;<strong>Allow TT to Manage Passwords for Ticket Sites</strong>
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;I consent to TT International managing my
            login credentials for ticket sites to handle account issues and
            fulfilment.
          </label>
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <label>
            <input
              type="checkbox"
              name="allow_tt_create_emails"
              checked={formData.allow_tt_create_emails}
              onChange={handleChange}
            />
            &nbsp;<strong>Allow TT to Create Additional Emails on My Behalf</strong>
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;I authorize TT International to create
            additional email addresses for my use to help manage my ticket
            purchasing and improve my queue positions.
          </label>
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <label>
            <input
              type="checkbox"
              name="live_rebate_updates"
              checked={formData.live_rebate_updates}
              onChange={handleChange}
            />
            &nbsp;<strong>Receive Live Updates on Rebates</strong>
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;I agree to receive live notifications and
            updates from TT International regarding my rebate status.
          </label>
        </div>

        {/* ---- Payment Details ---- */}
        <div style={{ margin: "1rem 0 0.5rem" }}>
          <strong>Payment Details</strong>
        </div>
        <TextInput
          label="Payment Full Name (required)"
          name="payment_full_name"
          value={formData.payment_full_name}
          onChange={handleChange}
        />
        <TextInput
          label="Payment Sort Code (required)"
          name="payment_sort_code"
          value={formData.payment_sort_code}
          onChange={handleChange}
        />
        <TextInput
          label="Payment Account Number (required)"
          name="payment_account_number"
          value={formData.payment_account_number}
          onChange={handleChange}
        />
        <TextInput
          label="User code of the person who referred you. (optional)"
          name="referral1"
          value={formData.referral1}
          onChange={handleChange}
        />

        {/* Referral 2 field still present in state but not displayed */}
        {/* <TextInput
          label="Referral 2 (optional)"
          name="referral2"
          value={formData.referral2}
          onChange={handleChange}
        /> */}

        {/* ---- Verification ---- */}
        <div style={{ margin: "1rem 0 0.5rem" }}>
          <strong>Verification</strong>
        </div>
        <FileUploader
          label="Please upload valid photo identification like a passport or driver’s license to verify your identity. (File upload feature)"
          onFileSelected={handleFileSelected}
        />
        <SignaturePad
          label="By signing below, I confirm that the information I have provided is accurate and that I have read and agreed to all terms, conditions, and permissions outlined in this form."
          onSignatureChange={handleSignatureChange}
        />

        {/* ---- Terms & Conditions (via Modal) ---- */}
        <div style={{ marginBottom: "1rem", marginTop: "1rem" }}>
          <label>
            <input
              type="checkbox"
              name="terms_accepted"
              checked={formData.terms_accepted}
              onChange={() => {}}
              disabled
            />
            &nbsp;I have read and accept the Terms & Conditions (required)
          </label>
          &nbsp;
          <button onClick={openModal} style={{ marginLeft: "6px" }}>
            View T&C & Privacy
          </button>
        </div>

        {/* ---- Submit ---- */}
        <button type="submit">Submit Form</button>
      </form>

      {isModalOpen && (
        <TermsModal onClose={closeModal} onAccept={acceptTerms} />
      )}
    </div>
  );
}

export default OnboardingNew;
